/**
 * CRM Data Refresh — quarterly campaign UI
 * ──────────────────────────────────────────
 * Lets staff:
 *   1. Build a recipient pool for the current quarter (merged across
 *      CRM + partner + archive + offer sources; opt-outs filtered).
 *   2. Send a single TEST email to themselves to review rendering.
 *   3. Approve + fire the bulk send (~5 emails/sec).
 *   4. Review opt-outs and the resulting "manually backfill" to-do list.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { API_URL } from "../config/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft, RefreshCw, Send, Loader2, CheckCircle2, AlertTriangle,
  Mail, Users, Ban, ListTodo,
} from "lucide-react";

const sourcesColors = {
  crm: "bg-blue-100 text-blue-700 border-blue-200",
  partner_network: "bg-violet-100 text-violet-700 border-violet-200",
  email_archive: "bg-amber-100 text-amber-700 border-amber-200",
  offer: "bg-emerald-100 text-emerald-700 border-emerald-200",
  quote_request: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function CRMDataRefreshPage() {
  const token = (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const userEmail = (typeof window !== "undefined" && JSON.parse(localStorage.getItem("user") || "{}").email) || "";

  const [campaigns, setCampaigns] = useState([]);
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [activeStatus, setActiveStatus] = useState({});
  const [creating, setCreating] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingBulk, setSendingBulk] = useState(false);
  const [optOuts, setOptOuts] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [testEmail, setTestEmail] = useState(userEmail);

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/crm-refresh/campaigns`, { headers: auth });
      setCampaigns(res.data?.campaigns || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load campaigns");
    }
  }, [auth]);

  const fetchOptOuts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/crm-refresh/opt-outs`, { headers: auth });
      setOptOuts(res.data?.opt_outs || []);
    } catch (e) { /* silent */ }
  }, [auth]);

  const fetchFollowups = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/crm-refresh/staff-followups`, { headers: auth });
      setFollowups(res.data?.followups || []);
    } catch (e) { /* silent */ }
  }, [auth]);

  const refreshCampaign = useCallback(async (id) => {
    if (!id) return;
    try {
      const res = await axios.get(`${API_URL}/crm-refresh/campaigns/${id}`, { headers: auth });
      setActiveCampaign(res.data?.campaign);
      setActiveStatus(res.data?.by_status || {});
    } catch (e) { /* silent */ }
  }, [auth]);

  useEffect(() => {
    fetchCampaigns();
    fetchOptOuts();
    fetchFollowups();
  }, [fetchCampaigns, fetchOptOuts, fetchFollowups]);

  const createCampaign = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/crm-refresh/campaigns`, {
        include_sources: ["crm", "partner_network", "email_archive", "offer"],
      }, { headers: auth });
      const c = res.data?.campaign;
      toast.success(`Campaign created with ${res.data?.recipient_count} recipients`);
      await fetchCampaigns();
      setActiveCampaign(c);
      await refreshCampaign(c.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const sendTest = async () => {
    if (!activeCampaign?.id) return;
    if (!testEmail || !testEmail.includes("@")) { toast.error("Enter your email for the test"); return; }
    setSendingTest(true);
    try {
      await axios.post(`${API_URL}/crm-refresh/campaigns/${activeCampaign.id}/send-test`, {
        to_email: testEmail,
      }, { headers: auth });
      toast.success(`Test email sent to ${testEmail}. Check your inbox before bulk send.`);
      await refreshCampaign(activeCampaign.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    } finally {
      setSendingTest(false);
    }
  };

  const sendBulk = async (batchLimit) => {
    if (!activeCampaign?.id) return;
    const pendingCount = activeStatus.pending ?? activeCampaign.recipient_count;
    const willSend = batchLimit ? Math.min(batchLimit, pendingCount) : pendingCount;
    if (!window.confirm(
      `About to send ${willSend} live emails (of ${pendingCount} pending). This cannot be undone. Continue?`
    )) return;
    setSendingBulk(true);
    try {
      const url = batchLimit
        ? `${API_URL}/crm-refresh/campaigns/${activeCampaign.id}/send?limit=${batchLimit}`
        : `${API_URL}/crm-refresh/campaigns/${activeCampaign.id}/send`;
      const res = await axios.post(url, {}, { headers: auth, timeout: 15 * 60 * 1000 });
      toast.success(
        `Sent: ${res.data?.sent} · Failed: ${res.data?.failed} · Remaining: ${res.data?.remaining_pending}`
      );
      await refreshCampaign(activeCampaign.id);
      await fetchCampaigns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk send failed");
    } finally {
      setSendingBulk(false);
    }
  };

  const closeFollowup = async (id) => {
    try {
      await axios.post(`${API_URL}/crm-refresh/staff-followups/${id}/done`, {}, { headers: auth });
      setFollowups((prev) => prev.filter((f) => f.id !== id));
    } catch (e) {
      toast.error("Failed to close");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="crm-refresh-page">
      <div className="max-w-7xl mx-auto">
        <Link to="/offers" className="text-sm text-slate-600 hover:text-slate-900 mb-4 inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> Back to Offers
        </Link>

        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <RefreshCw className="h-6 w-6 text-blue-600" /> CRM Data Refresh
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Quarterly campaign that asks every external contact (CRM, partners, archive, offers) to confirm
              their details. Opt-outs persist across quarters and trigger a "backfill from legacy" to-do for staff.
            </p>
          </div>
          <Button
            onClick={createCampaign}
            disabled={creating}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="crm-refresh-create-campaign"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> :
              <><RefreshCw className="h-4 w-4 mr-1" /> New campaign (this quarter)</>}
          </Button>
        </div>

        {/* Active campaign card */}
        {activeCampaign && (
          <Card className="p-6 mb-6" data-testid="crm-refresh-active-campaign">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                  {activeCampaign.quarter} · {activeCampaign.status?.toUpperCase()}
                </div>
                <h2 className="text-lg font-bold text-slate-900 mt-1">{activeCampaign.name}</h2>
                <div className="text-xs text-slate-500 mt-1">
                  Created by {activeCampaign.created_by} ·
                  {activeCampaign.recipient_count} recipients
                </div>
              </div>
            </div>

            {/* Status counters */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="text-xs text-slate-500">Total</div>
                <div className="text-xl font-bold text-slate-900">{activeCampaign.recipient_count}</div>
              </div>
              <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3">
                <div className="text-xs text-blue-700">Pending</div>
                <div className="text-xl font-bold text-blue-700">{activeStatus.pending ?? activeCampaign.recipient_count}</div>
              </div>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
                <div className="text-xs text-emerald-700">Sent</div>
                <div className="text-xl font-bold text-emerald-700">{activeStatus.sent ?? 0}</div>
              </div>
              <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3">
                <div className="text-xs text-violet-700">Replied</div>
                <div className="text-xl font-bold text-violet-700">{activeStatus.replied ?? 0}</div>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50/40 p-3">
                <div className="text-xs text-red-700">Opted out</div>
                <div className="text-xl font-bold text-red-700">{activeStatus.opted_out ?? 0}</div>
              </div>
            </div>

            {/* Test send */}
            <div className="rounded-lg border border-amber-200 bg-amber-50/30 p-4 mb-3">
              <div className="font-semibold text-amber-900 text-sm mb-1 flex items-center gap-1">
                <Mail className="h-4 w-4" /> Step 1 — Send a test to yourself
              </div>
              <p className="text-xs text-slate-600 mb-3">
                You'll receive the exact email recipients will get, with a working opt-out link
                and the public update-form link. Reply to confirm it looks right.
              </p>
              <div className="flex items-center gap-2">
                <Input
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="your.email@europacific.com"
                  className="max-w-sm"
                  data-testid="crm-refresh-test-email-input"
                />
                <Button
                  onClick={sendTest}
                  disabled={sendingTest}
                  variant="outline"
                  data-testid="crm-refresh-send-test"
                >
                  {sendingTest ? <Loader2 className="h-4 w-4 animate-spin" /> :
                    <><Send className="h-4 w-4 mr-1" /> Send test</>}
                </Button>
                {activeCampaign.test_sent_at && (
                  <span className="text-xs text-emerald-700 flex items-center gap-1">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Test sent {new Date(activeCampaign.test_sent_at).toLocaleString()}
                  </span>
                )}
              </div>
            </div>

            {/* Bulk send — graduated rollout */}
            <div className="rounded-lg border border-red-200 bg-red-50/30 p-4">
              <div className="font-semibold text-red-900 text-sm mb-1 flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" /> Step 2 — Fire in batches (graduated rollout)
              </div>
              <p className="text-xs text-slate-600 mb-3">
                Sends ~5 emails/sec via M365 Graph. Use small batches first to give the
                <code className="px-1 mx-1 bg-slate-100 rounded text-xs">roket@europacific.com</code>
                mailbox time to build sender reputation with Hotmail/Gmail.
                Recommended: 50 → 250 → rest, spaced 1-3 days apart.
                <b> Only enabled after a test send.</b>
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  onClick={() => sendBulk(50)}
                  disabled={sendingBulk || !activeCampaign.test_sent_at || (activeStatus.pending ?? activeCampaign.recipient_count) === 0}
                  variant="outline"
                  className="border-amber-300 text-amber-800 hover:bg-amber-50"
                  data-testid="crm-refresh-send-50"
                >
                  {sendingBulk ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                  Send next 50
                </Button>
                <Button
                  onClick={() => sendBulk(250)}
                  disabled={sendingBulk || !activeCampaign.test_sent_at || (activeStatus.pending ?? activeCampaign.recipient_count) === 0}
                  variant="outline"
                  className="border-orange-300 text-orange-800 hover:bg-orange-50"
                  data-testid="crm-refresh-send-250"
                >
                  {sendingBulk ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                  Send next 250
                </Button>
                <Button
                  onClick={() => sendBulk(1000)}
                  disabled={sendingBulk || !activeCampaign.test_sent_at || (activeStatus.pending ?? activeCampaign.recipient_count) === 0}
                  variant="outline"
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="crm-refresh-send-1000"
                >
                  {sendingBulk ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                  Send next 1,000
                </Button>
                <Button
                  onClick={() => sendBulk(null)}
                  disabled={sendingBulk || !activeCampaign.test_sent_at || (activeStatus.pending ?? activeCampaign.recipient_count) === 0}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="crm-refresh-send-bulk"
                >
                  {sendingBulk ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Sending…</> :
                    activeCampaign.status === "sent" ?
                      <><CheckCircle2 className="h-4 w-4 mr-1" /> All sent</> :
                      <><Send className="h-4 w-4 mr-1" /> Send all remaining ({activeStatus.pending ?? activeCampaign.recipient_count})</>}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Past campaigns + opt-outs + staff to-dos */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Past campaigns */}
          <Card className="p-4" data-testid="crm-refresh-history">
            <div className="font-semibold text-slate-900 mb-3 flex items-center gap-1">
              <Users className="h-4 w-4 text-blue-600" /> Campaign history
            </div>
            {campaigns.length === 0 ? (
              <div className="text-sm text-slate-400 italic">No campaigns yet.</div>
            ) : (
              <div className="space-y-2">
                {campaigns.map((c) => (
                  <button key={c.id}
                    onClick={() => { setActiveCampaign(c); refreshCampaign(c.id); }}
                    className="w-full text-left rounded-lg border border-slate-200 hover:bg-slate-50 p-3"
                    data-testid={`crm-refresh-history-item-${c.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-sm text-slate-900">{c.quarter}</div>
                        <div className="text-xs text-slate-500">{c.recipient_count} recipients</div>
                      </div>
                      <Badge variant="outline" className="text-xs">{c.status}</Badge>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Card>

          {/* Opt-outs */}
          <Card className="p-4" data-testid="crm-refresh-opt-outs">
            <div className="font-semibold text-slate-900 mb-3 flex items-center gap-1">
              <Ban className="h-4 w-4 text-red-600" /> Opt-outs ({optOuts.length})
            </div>
            {optOuts.length === 0 ? (
              <div className="text-sm text-slate-400 italic">No one has opted out yet.</div>
            ) : (
              <div className="max-h-72 overflow-y-auto space-y-1">
                {optOuts.map((o) => (
                  <div key={o.email} className="text-xs border-b border-slate-100 py-1.5">
                    <div className="font-mono text-slate-700">{o.email}</div>
                    <div className="text-slate-400">
                      {o.opted_out_at ? new Date(o.opted_out_at).toLocaleDateString() : ""}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Staff follow-ups */}
          <Card className="p-4" data-testid="crm-refresh-followups">
            <div className="font-semibold text-slate-900 mb-3 flex items-center gap-1">
              <ListTodo className="h-4 w-4 text-amber-600" /> Manual backfill to-do ({followups.length})
            </div>
            {followups.length === 0 ? (
              <div className="text-sm text-slate-400 italic">No follow-ups pending.</div>
            ) : (
              <div className="max-h-72 overflow-y-auto space-y-2">
                {followups.map((f) => (
                  <div key={f.id} className="rounded border border-slate-200 p-2 text-xs">
                    <div className="font-mono text-slate-700">{f.email}</div>
                    {f.company_name && <div className="text-slate-500">{f.company_name}</div>}
                    {f.sources_we_had?.length > 0 && (
                      <div className="mt-1 flex gap-1 flex-wrap">
                        {f.sources_we_had.map((s) => (
                          <span key={s} className={`px-1.5 py-0.5 rounded text-[10px] border ${sourcesColors[s] || ""}`}>{s}</span>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={() => closeFollowup(f.id)}
                      className="mt-2 text-[11px] text-blue-600 hover:underline"
                      data-testid={`crm-refresh-followup-done-${f.id}`}
                    >
                      Mark backfilled →
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
